import fs from 'node:fs';
import path from 'node:path';
import childProcess from 'node:child_process';

import { expand } from 'dotenv-expand';
import type { ArgumentsCamelCase, InferredOptionTypes } from 'yargs';

export const yargsOptionsBuilderForEnv = {
  'cascade-env': {
    description:
      'Environment (fnox profile / mise env) to load environment variables for. Preferred over `cascade-node-env` and `auto-cascade-env`.',
    type: 'string',
  },
  'cascade-node-env': {
    description: 'Same with --cascade-env=<NODE_ENV || "development">. Preferred over `auto-cascade-env`.',
    type: 'boolean',
  },
  'auto-cascade-env': {
    description: 'Same with --cascade-env=<WB_ENV || NODE_ENV || "development">.',
    type: 'boolean',
    default: true,
  },
  'quiet-env': {
    description: 'Suppress environment variable loading information.',
    type: 'boolean',
  },
  verbose: {
    description: 'Whether to show verbose information',
    type: 'boolean',
    alias: 'v',
  },
} as const;

export type EnvReaderOptions = Partial<ArgumentsCamelCase<InferredOptionTypes<typeof yargsOptionsBuilderForEnv>>> & {
  /**
   * Command-level fallback for an unset WB_ENV (e.g. `wb test` supplies 'test' when explicit env
   * flags suppress its default test cascade). Not a CLI flag.
   */
  commandDefaultWbEnv?: string;
};

const standardWbEnvModes = new Set(['development', 'test', 'staging', 'production']);

/**
 * Resolves the cascade (fnox profile / env-file suffix) the reader loads for the given options:
 * the forced `--cascade-env` first, then `--cascade-node-env`'s NODE_ENV, then the auto cascade
 * driven by the ambient WB_ENV/NODE_ENV. Exported so commands that select a profile WITHOUT
 * loading environment sources (e.g. `wb gen-docker-env`) cannot drift from the reader's selection.
 */
export function resolveCascade(argv: EnvReaderOptions): string | undefined {
  // Read NODE_ENV through an alias, never as the `process.env.NODE_ENV` member expression:
  // bundlers replace that exact expression at build time (see readEnvironmentVariables).
  const runtimeEnv = process.env;
  return (
    argv.cascadeEnv ??
    (argv.cascadeNodeEnv
      ? runtimeEnv.NODE_ENV || 'development'
      : argv.autoCascadeEnv
        ? runtimeEnv.WB_ENV || runtimeEnv.NODE_ENV || 'development'
        : undefined)
  );
}

/**
 * Resolves the WB_ENV value wb falls back to when no env source and no exported variable defines
 * it: the command-level default first (`wb test --cascade-env=staging` loads the staging files
 * but its tests must still run as `test`, mirroring the pre-15 `||= 'test'` behavior), then the
 * forced cascade, then the ambient-NODE_ENV-driven auto cascade clamped to a standard mode (a
 * non-standard NODE_ENV such as `qa` still selects the cascade suffix, but must not produce a
 * non-standard WB_ENV).
 */
export function resolveFallbackWbEnv(argv: EnvReaderOptions): string {
  if (argv.commandDefaultWbEnv) return argv.commandDefaultWbEnv;
  if (argv.cascadeEnv) return argv.cascadeEnv;
  // Read NODE_ENV through the alias for the same bundler-inlining reason as in
  // readEnvironmentVariables, and from the AMBIENT environment (not loaded files) because the
  // cascade selection below uses the ambient value as well.
  const runtimeEnv = process.env;
  const derived =
    argv.cascadeNodeEnv || argv.autoCascadeEnv !== false ? runtimeEnv.NODE_ENV || 'development' : 'development';
  return standardWbEnvModes.has(derived) ? derived : 'development';
}

/**
 * This function reads environment variables from the repository's fnox configuration and mise.
 * Note it does not assign them in `process.env`.
 * @return [envVars, [envSourceNames, envVarNames][]]
 * */
export function readEnvironmentVariables(
  argv: EnvReaderOptions,
  cwd: string,
  options?: {
    /**
     * Load variables even if they already exist in process.env.
     * Useful when a parent process has already injected the fnox values into the environment
     * and the configured variables themselves are needed (e.g. `wb gen-dev-vars`).
     */
    ignoreProcessEnv?: boolean;
    /**
     * Expand `${WB_ENV}` references against the fallback mode when nothing defines WB_ENV.
     * Only for callers that subsequently COMPLETE WB_ENV with that fallback (wb's Project.env).
     */
    expandFallbackWbEnv?: boolean;
  }
): [Record<string, string>, [string, string[]][]] {
  // Read NODE_ENV through an alias, never as the `process.env.NODE_ENV` member expression:
  // bundlers replace that exact expression at build time (rolldown/build-ts inline it as
  // 'production'), which constant-folds the fallback below and made the published wb select
  // the production profile whenever WB_ENV was unset.
  const runtimeEnv = process.env;
  const cascade = resolveCascade(argv);
  const shouldSuppressOutput = shouldSuppressEnvironmentOutput(argv);
  if (argv.verbose && !shouldSuppressOutput) {
    console.info(`WB_ENV: ${runtimeEnv.WB_ENV}, NODE_ENV: ${runtimeEnv.NODE_ENV}`);
  }

  // When the caller explicitly forces a mode (--cascade-env / --cascade-node-env / an exported
  // WB_ENV), values that the mode's own fnox profile defines must win over variables inherited
  // from the parent shell: a stale `export DATABASE_URL=...` from a development shell must not
  // leak into `wb test`'s test mode (cf. https://github.com/WillBooster/shared/issues/930). On CI
  // the inherited variables keep winning — workflows deliberately inject env vars that override
  // the committed values — and that shadowing is the designed behavior.
  const modeIsForced = Boolean(
    argv.cascadeEnv ??
    (argv.cascadeNodeEnv ? runtimeEnv.NODE_ENV || 'development' : argv.autoCascadeEnv ? runtimeEnv.WB_ENV : undefined)
  );
  const modeFileOverridesProcessEnv = modeIsForced && !isCIEnvironment(runtimeEnv.CI);

  const envPathAndLoadedEnvVarNames: [string, string[]][] = [];
  const envVars: Record<string, string> = {};
  const projectHasFnoxConfig = hasProjectFnoxConfig(cwd);
  const [fnoxEnvVars, fnoxEnvVarNames] = readFnoxEnvironmentVariables(cwd, cascade, {
    ...options,
    modeFileOverridesProcessEnv,
    hasFnoxConfig: projectHasFnoxConfig,
  });
  Object.assign(envVars, fnoxEnvVars);
  // Report the fnox source whenever fnox.toml exists — even when it yields no keys (all shadowed,
  // empty profile, or a failing export): consumers such as wb's required-environment validation
  // must see that a declared env source exists rather than silently failing open.
  if (fnoxEnvVarNames.length > 0 || projectHasFnoxConfig) {
    const fnoxSourceName = fnoxEnvironmentSourceName(cascade);
    envPathAndLoadedEnvVarNames.push([fnoxSourceName, fnoxEnvVarNames]);
    if (argv.verbose && !shouldSuppressOutput) {
      console.info(`Read ${fnoxEnvVarNames.length} environment variables from ${fnoxSourceName}`);
    }
  }
  const [miseEnvVars, miseEnvVarNames] = readMiseEnvironmentVariables(cwd, cascade, envVars, options);
  Object.assign(envVars, miseEnvVars);
  if (miseEnvVarNames.length > 0) {
    const miseSourceName = miseEnvironmentSourceName(cascade);
    envPathAndLoadedEnvVarNames.push([miseSourceName, miseEnvVarNames]);
    if (argv.verbose && !shouldSuppressOutput) {
      console.info(`Read ${miseEnvVarNames.length} environment variables from ${miseSourceName}`);
    }
  }
  if (!argv.verbose && !shouldSuppressOutput) {
    console.info(
      `Read env sources: ${envPathAndLoadedEnvVarNames.map(([envPath, keys]) => (keys.length > 0 ? `${envPath} (${keys.join(', ')})` : envPath)).join(', ') || 'nothing'}`
    );
  }

  // Expand references against the live environment for keys NOT loaded from files, so that a
  // reference to an exported key (excluded from envVars by process-env precedence) resolves to
  // the effective value instead of an empty string. Loaded keys are deliberately absent from
  // the reference set: dotenv-expand would otherwise replace their parsed values with the
  // process values, breaking callers that need the file-defined values themselves.
  const referenceEnv: Record<string, string> = {};
  for (const [key, value] of Object.entries(process.env)) {
    // Escape dollar signs so dotenv-expand substitutes exported values literally instead of
    // recursively re-expanding them (an exported `pa$word` must stay `pa$word`).
    if (value !== undefined && !(key in envVars)) referenceEnv[key] = value.replaceAll('$', String.raw`\$`);
  }
  // dotenv-expand resolves references in key-insertion order, so a fnox value referencing a
  // mise-provided key would see an empty string if the mise entries stayed appended after the
  // fnox entries. Rebuild the expansion input with the lower-priority sources first; the values
  // themselves already reflect the intended fnox-over-mise precedence.
  const orderedEnvVars: Record<string, string> = {};
  for (const key of [...miseEnvVarNames, ...fnoxEnvVarNames]) orderedEnvVars[key] = envVars[key]!;
  Object.assign(orderedEnvVars, envVars);
  // expand() mutates BOTH its parsed input AND processEnv (dotenv-expand writes every parsed
  // result into processEnv), so snapshot both for the re-expansion below — the retry must not
  // see the first pass's stale dependent values.
  const preExpansionEnvVars = { ...orderedEnvVars };
  const pristineReferenceEnv = { ...referenceEnv };
  let expandedEnvVars = expand({ parsed: orderedEnvVars, processEnv: referenceEnv }).parsed ?? orderedEnvVars;
  // A value referencing ${WB_ENV} must expand to what the child will actually see: when the
  // EFFECTIVE WB_ENV ends up empty — whether it was never defined, defined empty, or emptied by
  // the expansion itself (e.g. `WB_ENV=${MISSING_MODE}`) — wb's Project.env later fills it with
  // the fallback mode, so re-expand from the original values with that fallback available.
  // Opt-in via the option: a direct readEnvironmentVariables/readAndApplyEnvironmentVariables
  // caller never receives the completed WB_ENV, and expanding references against a value that is
  // not actually applied would make the pair inconsistent.
  // The loaded key masks the ambient value, so a loaded-but-emptied WB_ENV needs the retry even
  // when an exported WB_ENV exists (a forced mode file overrides the export locally): retry with
  // the value the merged environment will ultimately retain.
  const effectiveWbEnv = expandedEnvVars.WB_ENV ?? runtimeEnv.WB_ENV;
  if (options?.expandFallbackWbEnv && !effectiveWbEnv) {
    const reExpansionInput = { ...preExpansionEnvVars };
    delete reExpansionInput.WB_ENV;
    const retryReferenceEnv = { ...pristineReferenceEnv, WB_ENV: runtimeEnv.WB_ENV || resolveFallbackWbEnv(argv) };
    expandedEnvVars = expand({ parsed: reExpansionInput, processEnv: retryReferenceEnv }).parsed ?? reExpansionInput;
  }
  return [expandedEnvVars, envPathAndLoadedEnvVarNames];
}

/**
 * This function reads environment variables from the repository's fnox configuration (`fnox.toml`).
 * The base `[secrets]` table carries the development values, and `[profiles.<cascade>.secrets]`
 * overlays it; an unknown profile falls back to the base secrets.
 */
export function readFnoxEnvironmentVariables(
  cwd: string,
  cascade: string | undefined,
  options?: {
    ignoreProcessEnv?: boolean;
    modeFileOverridesProcessEnv?: boolean;
    /** Precomputed hasProjectFnoxConfig(cwd) result, to avoid re-walking the ancestor directories. */
    hasFnoxConfig?: boolean;
  }
): [Record<string, string>, string[]] {
  if (!(options?.hasFnoxConfig ?? hasProjectFnoxConfig(cwd))) return [{}, []];

  const secrets = runFnoxExport(cwd, cascade, { quiet: false });
  if (!secrets) return [{}, []];
  // `[profiles.<cascade>.secrets]` is the fnox analogue of `.env.<cascade>`: when the caller
  // forces a mode off CI, profile-declared values must override inherited shell variables just
  // like `.env.<mode>` values do, while base `[secrets]` values keep losing to process.env.
  // A key's value is profile-specific under EITHER criterion, because neither alone covers both
  // shapes: `--no-defaults` omits the base `[secrets]` table, so its key set is exactly the
  // profile's own declarations — including one repeating the base value, which a value comparison
  // cannot see (https://github.com/WillBooster/shared/issues/1080) — while a base entry
  // interpolating a profile-overridden key (`DATABASE_URL = "postgres://${DB_HOST}/app"`) stays
  // out of that key set although its exported value differs from the base export's.
  // Both exports run LAZILY, only when a process.env collision actually needs adjudicating — they
  // would otherwise add subprocesses (including age decryption) to every forced-mode invocation
  // for nothing — and a failing export simply disables its own criterion (conservative).
  // The omitted base table also makes fnox reject a profile default REFERENCING a base secret
  // (`URL = { default = "https://${HOST}/x" }`), which the repository rules therefore forbid; the
  // warning names that rule because the lost precision would otherwise be invisible.
  let cachedProfileKeys: Set<string> | undefined | false = false;
  const getProfileKeys = (): Set<string> | undefined => {
    if (cachedProfileKeys === false) {
      const profileSecrets = runFnoxExport(cwd, cascade, { quiet: false, profileOnly: true });
      cachedProfileKeys = profileSecrets && new Set(Object.keys(profileSecrets));
    }
    return cachedProfileKeys;
  };
  let cachedBaseSecrets: Record<string, unknown> | undefined | false = false;
  const overridesProcessEnv = (key: string, value: string): boolean => {
    if (getProfileKeys()?.has(key)) return true;
    if (cachedBaseSecrets === false) {
      cachedBaseSecrets = runFnoxExport(cwd, undefined, { quiet: true, ignoreProfileEnvVar: true });
    }
    return cachedBaseSecrets !== undefined && cachedBaseSecrets[key] !== value;
  };

  const envVars: Record<string, string> = {};
  const keys: string[] = [];
  for (const [key, value] of Object.entries(secrets)) {
    if (typeof value !== 'string') continue;
    // Explicitly exported environment variables win over fnox base values.
    // (The mise reader below intentionally uses a value-equality check instead: `mise env` echoes
    // back variables the ambient mise activation already exported, and a differing value means the
    // requested cascade profile should win over the stale activation.)
    if (
      !options?.ignoreProcessEnv &&
      key in process.env &&
      !(options?.modeFileOverridesProcessEnv && cascade && overridesProcessEnv(key, value))
    ) {
      continue;
    }
    envVars[key] = value;
    keys.push(key);
  }
  return [envVars, keys];
}

function runFnoxExport(
  cwd: string,
  cascade: string | undefined,
  options: { quiet: boolean; profileOnly?: boolean; ignoreProfileEnvVar?: boolean }
): Record<string, unknown> | undefined {
  // `--if-missing error` (default): fnox otherwise exits 0 and silently omits secrets it fails to
  // resolve (e.g. a missing age key), which would be indistinguishable from undeclared secrets.
  // Opt-in escape hatch `WB_ALLOW_MISSING_SECRETS=1`: switch to `warn` so a keyless context — e.g. a
  // Docker/Railway image build that only inlines non-secret public values (`APP_TITLE`,
  // `NEXT_PUBLIC_*`) while the age key is deliberately absent — still loads the resolvable values
  // (plaintext defaults plus any secret that DID decrypt) instead of failing outright. `warn`, not
  // `ignore`, keeps fnox reporting the skipped secret names on stderr (surfaced below), so relaxing
  // the check stays auditable rather than silently dropping declared secrets.
  // `--non-interactive`: prompts or browser auth flows would hang forever because stdin is ignored.
  const allowMissingSecrets =
    process.env.WB_ALLOW_MISSING_SECRETS === '1' || process.env.WB_ALLOW_MISSING_SECRETS === 'true';
  const args = [
    'export',
    '--format',
    'json',
    '--no-color',
    '--if-missing',
    allowMissingSecrets ? 'warn' : 'error',
    '--non-interactive',
  ];
  if (cascade) {
    args.push('--profile', cascade);
  }
  if (options.profileOnly) {
    args.push('--no-defaults');
  }
  const env = { ...process.env };
  if (options.ignoreProfileEnvVar) {
    // Without `--profile`, fnox falls back to FNOX_PROFILE; the base-adjudication export must
    // read the BASE secrets, so the inherited profile selection is cleared for it — and only for
    // it. A PRIMARY export still honors FNOX_PROFILE: it either stays profile-less (fnox reads the
    // variable) or folds it into the `--profile` it passes (see wb dotenv's cascade).
    delete env.FNOX_PROFILE;
  }
  const result = childProcess.spawnSync('fnox', args, {
    cwd,
    env,
    encoding: 'utf8',
    stdio: ['ignore', 'pipe', 'pipe'],
  });
  if (result.error || result.status !== 0 || !result.stdout?.trim()) {
    // The repository declares fnox-managed secrets (fnox.toml exists), so a failing export must be
    // surfaced: swallowing it would make declared secrets indistinguishable from undeclared ones.
    // The profile-only export reports what its failure costs plus its likeliest cause, and always
    // quotes fnox's own error because other causes (e.g. a fnox too old for `--no-defaults`) look
    // identical from here.
    if (!options.quiet) {
      const reason = result.error?.message || result.stderr?.trim() || `fnox exited with status ${result.status}`;
      console.warn(
        options.profileOnly
          ? `Failed to read the "${cascade}" fnox profile's own secrets, so its values equal to the base values do not override the inherited environment variables. Make the defaults in [profiles.${cascade}.secrets] independent of the base [secrets] table. ${reason}`
          : `Failed to read fnox secrets: ${reason}`
      );
    }
    return;
  }

  if (allowMissingSecrets && !options.quiet && result.stderr?.trim()) {
    // With `--if-missing warn`, fnox exits 0 but reports the secrets it could not resolve on stderr.
    // Surface them so relaxing the check via WB_ALLOW_MISSING_SECRETS does not hide a real gap.
    console.warn(`WB_ALLOW_MISSING_SECRETS: continuing without unresolved fnox secrets.\n${result.stderr.trim()}`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(result.stdout);
  } catch {
    return;
  }
  const secrets = (parsed as { secrets?: unknown } | undefined)?.secrets;
  if (!secrets || typeof secrets !== 'object' || Array.isArray(secrets)) return;
  return secrets as Record<string, unknown>;
}

export function hasProjectFnoxConfig(cwd: string): boolean {
  return hasAncestorContaining(cwd, ['fnox.toml']);
}

function fnoxEnvironmentSourceName(cascade: string | undefined): string {
  return cascade ? `fnox export --profile ${cascade}` : 'fnox export';
}

function readMiseEnvironmentVariables(
  cwd: string,
  cascade: string | undefined,
  currentEnvVars: Record<string, string>,
  options?: { ignoreProcessEnv?: boolean }
): [Record<string, string>, string[]] {
  if (!hasProjectMiseConfig(cwd)) return [{}, []];

  const args = ['env', '--json', '--cd', cwd];
  if (cascade) {
    args.push('--env', cascade);
  }
  const result = childProcess.spawnSync('mise', args, {
    encoding: 'utf8',
    stdio: ['ignore', 'pipe', 'pipe'],
  });
  if (result.error || result.status !== 0 || !result.stdout?.trim()) return [{}, []];

  let parsed: unknown;
  try {
    parsed = JSON.parse(result.stdout);
  } catch {
    return [{}, []];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [{}, []];

  const envVars: Record<string, string> = {};
  const keys: string[] = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== 'string' || key in currentEnvVars) continue;
    if (options?.ignoreProcessEnv) {
      // `mise env` always emits PATH due to tool shims; consumers of file-defined variables
      // (e.g. `wb gen-dev-vars`) must not propagate it.
      if (key === 'PATH') continue;
    } else if (process.env[key] === value) {
      continue;
    }
    envVars[key] = value;
    keys.push(key);
  }
  return [envVars, keys];
}

function hasProjectMiseConfig(cwd: string): boolean {
  return hasAncestorContaining(cwd, ['mise.toml', '.mise.toml']);
}

function hasAncestorContaining(cwd: string, fileNames: string[]): boolean {
  for (let currentPath = path.resolve(cwd); ; currentPath = path.dirname(currentPath)) {
    if (fileNames.some((fileName) => fs.existsSync(path.join(currentPath, fileName)))) {
      return true;
    }
    const parentPath = path.dirname(currentPath);
    if (parentPath === currentPath) return false;
  }
}

function miseEnvironmentSourceName(cascade: string | undefined): string {
  return cascade ? `mise env --env ${cascade}` : 'mise env';
}

function isCIEnvironment(ciEnv: string | undefined): boolean {
  return !!ciEnv && ciEnv !== '0' && ciEnv !== 'false';
}

export function shouldSuppressEnvironmentOutput(argv: EnvReaderOptions): boolean {
  const outputOptions = argv as EnvReaderOptions & { quietEnv?: boolean; silent?: boolean };
  return outputOptions.quietEnv === true || (outputOptions.quietEnv !== false && outputOptions.silent === true);
}

/**
 * This function reads environment variables from fnox/mise and assigns them in `process.env`.
 * */
export function readAndApplyEnvironmentVariables(
  argv: EnvReaderOptions,
  cwd: string
): Record<string, string | undefined> {
  const [envVars] = readEnvironmentVariables(argv, cwd);
  for (const [key, value] of Object.entries(envVars)) {
    // Existing process.env keys are kept: envVars may deliberately contain differing values that
    // must win only in the returned record (mise cascade-profile values, forced-mode overrides
    // consumed via `project.env`), never clobber the caller's own process environment.
    if (!(key in process.env)) {
      process.env[key] = value;
    }
  }
  return envVars;
}

/**
 * This function removes environment variables related to npm and yarn from the given environment variables.
 * */
export function removeNpmAndYarnEnvironmentVariables(envVars: Record<string, string | undefined>): void {
  if (envVars.PATH && envVars.BERRY_BIN_FOLDER) {
    envVars.PATH = envVars.PATH.replace(`${envVars.BERRY_BIN_FOLDER}:`, '')
      // Temporary directory in macOS
      .replaceAll(/\/private\/var\/folders\/[^:]+:/g, '')
      // Temporary directories in Linux
      .replaceAll(/\/var\/tmp\/[^:]+:/g, '')
      .replaceAll(/\/tmp\/[^:]+:/g, '');
  }
  for (const key of Object.keys(envVars)) {
    const upperKey = key.toUpperCase();
    if (
      upperKey.startsWith('NPM_') ||
      upperKey.startsWith('YARN_') ||
      upperKey.startsWith('BERRY_') ||
      upperKey === 'PROJECT_CWD' ||
      upperKey === 'INIT_CWD'
    ) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete envVars[key];
    }
  }
}
